import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { ShieldCheck, ShieldAlert, KeyRound, RefreshCw, Trash2 } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Badge } from '@/components/badge';
import { Dialog } from '@/components/dialog';
import { useToast } from '@/components/toast';
import {
  useMe,
  useEnableTwoFactor,
  useConfirmTwoFactor,
  useDisableTwoFactor,
  useTwoFactorQrCode,
  useTwoFactorSecret,
  useTwoFactorRecoveryCodes,
  useRegenerateRecoveryCodes,
  useUpdatePassword,
  useConfirmPassword,
  isPasswordConfirmed,
} from '@/lib/auth';
import { useFormError } from '@/lib/form-error';

/**
 * Fortify guards the 2FA management endpoints with the password.confirm
 * middleware. This gate runs a protected action, prompting for the password
 * first when the session hasn't confirmed it inside the timeout window.
 */
type ProtectedAction = () => Promise<unknown>;
const PasswordGateContext = createContext<(action: ProtectedAction) => Promise<void>>(
  async (action) => {
    await action();
  },
);

function usePasswordGate() {
  return useContext(PasswordGateContext);
}

function PasswordGateProvider({ children }: { children: ReactNode }) {
  const confirmPassword = useConfirmPassword();
  const toast = useToast();
  const [resolver, setResolver] = useState<((ok: boolean) => void) | null>(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const gate = useCallback(
    async (action: ProtectedAction) => {
      let confirmed: boolean;
      try {
        confirmed = await isPasswordConfirmed();
      } catch {
        confirmed = false;
      }
      if (!confirmed) {
        const ok = await new Promise<boolean>((resolve) => setResolver(() => resolve));
        if (!ok) return;
      }
      try {
        await action();
      } catch {
        toast.error('Something went wrong', 'Please try again.');
      }
    },
    [toast],
  );

  function close(ok: boolean) {
    resolver?.(ok);
    setResolver(null);
    setPassword('');
    setError(null);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await confirmPassword.mutateAsync(password);
    } catch {
      setError('That password is incorrect.');
      return;
    }
    close(true);
  }

  return (
    <PasswordGateContext.Provider value={gate}>
      {children}
      <Dialog open={resolver !== null} onClose={() => close(false)} title="Confirm your password">
        <form onSubmit={submit} className="space-y-3">
          <p className="text-sm text-[var(--color-foreground-muted)]">
            For your security, confirm your password to change two-factor settings.
          </p>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            required
            autoComplete="current-password"
            placeholder="Current password"
          />
          {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => close(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={confirmPassword.isPending || !password}>
              {confirmPassword.isPending ? 'Confirming…' : 'Confirm'}
            </Button>
          </div>
        </form>
      </Dialog>
    </PasswordGateContext.Provider>
  );
}

export function SecurityTab() {
  const me = useMe();

  if (!me.data) {
    return <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>;
  }

  const status: 'inactive' | 'pending' | 'active' = me.data.two_factor_enabled
    ? 'active'
    : me.data.two_factor_pending
      ? 'pending'
      : 'inactive';

  return (
    <PasswordGateProvider>
      <div className="space-y-6">
        <PasswordCard />
        <Card>
          <CardHeader className="flex items-start justify-between">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold">
              Two-factor authentication
              {status === 'active' ? (
                <Badge tone="success">
                  <ShieldCheck className="mr-1 size-3" />
                  Active
                </Badge>
              ) : status === 'pending' ? (
                <Badge tone="warning">
                  <ShieldAlert className="mr-1 size-3" />
                  Pending confirmation
                </Badge>
              ) : (
                <Badge tone="neutral">Off</Badge>
              )}
            </h3>
            <p className="mt-1 text-xs text-[var(--color-foreground-muted)]">
              Use an authenticator app (1Password, Authy, Google Authenticator) for a 6-digit code
              on every login.
            </p>
          </div>
        </CardHeader>
          <CardBody>
            {status === 'inactive' ? <InactiveView /> : null}
            {status === 'pending' ? <PendingView /> : null}
            {status === 'active' ? <ActiveView /> : null}
          </CardBody>
        </Card>
      </div>
    </PasswordGateProvider>
  );
}

function PasswordCard() {
  const update = useUpdatePassword();
  const { error, wrap } = useFormError();
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const ok = await wrap(() =>
      update.mutateAsync({
        current_password: current,
        password: next,
        password_confirmation: confirm,
      }),
    );
    if (ok) {
      toast.success('Password updated');
      setCurrent('');
      setNext('');
      setConfirm('');
    }
  }

  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold">Password</h3>
        <p className="mt-0.5 text-xs text-[var(--color-foreground-muted)]">
          Change the password you use to sign in.
        </p>
      </CardHeader>
      <CardBody>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <Field label="Current password">
            <Input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoComplete="current-password"
            />
          </Field>
          <Field label="New password">
            <Input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              autoComplete="new-password"
            />
          </Field>
          <Field label="Confirm new password">
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              autoComplete="new-password"
            />
          </Field>
          {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
          <div className="flex justify-end">
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Updating…' : 'Update password'}
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-[var(--color-foreground-muted)]">{label}</span>
      {children}
    </label>
  );
}

function InactiveView() {
  const enable = useEnableTwoFactor();
  const gate = usePasswordGate();

  return (
    <div className="space-y-3">
      <p className="text-sm">Adds a second step at sign-in. Strongly recommended.</p>
      <Button
        disabled={enable.isPending}
        onClick={() => gate(() => enable.mutateAsync())}
      >
        {enable.isPending ? 'Enabling…' : 'Enable two-factor'}
      </Button>
    </div>
  );
}

function PendingView() {
  const qr = useTwoFactorQrCode();
  const secret = useTwoFactorSecret();
  const confirm = useConfirmTwoFactor();
  const disable = useDisableTwoFactor();
  const { error, wrap } = useFormError();
  const [code, setCode] = useState('');
  const [showCodes, setShowCodes] = useState(false);

  useEffect(() => {
    qr.refetch();
    secret.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleConfirm(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const ok = await wrap(() => confirm.mutateAsync({ code }));
    if (ok) {
      setCode('');
      setShowCodes(true);
    }
  }

  return (
    <div className="space-y-5">
      <ol className="list-decimal space-y-2 pl-5 text-sm">
        <li>Scan the QR code with your authenticator app.</li>
        <li>Enter the 6-digit code below to confirm.</li>
      </ol>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div className="flex items-center justify-center rounded-md border border-[var(--color-border)] bg-white p-3">
          {qr.data ? (
            <div
              className="size-48"
              dangerouslySetInnerHTML={{ __html: qr.data }}
            />
          ) : (
            <p className="text-xs text-[var(--color-foreground-muted)]">Loading QR…</p>
          )}
        </div>
        <div className="space-y-2 text-sm">
          <p className="text-[var(--color-foreground-muted)]">
            Can't scan? Add this secret manually:
          </p>
          <code className="block break-all rounded bg-[var(--color-surface-muted)] px-2 py-1.5 text-xs">
            {secret.data ?? '…'}
          </code>
        </div>
      </div>

      <form className="space-y-2" onSubmit={handleConfirm}>
        <label className="block space-y-1.5">
          <span className="text-xs font-medium text-[var(--color-foreground-muted)]">
            Authenticator code
          </span>
          <Input
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="123456"
            required
            autoFocus
          />
        </label>
        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="flex justify-between">
          <Button
            variant="ghost"
            type="button"
            onClick={() => disable.mutate()}
            disabled={disable.isPending}
          >
            Cancel setup
          </Button>
          <Button type="submit" disabled={confirm.isPending || code.length !== 6}>
            {confirm.isPending ? 'Confirming…' : 'Confirm'}
          </Button>
        </div>
      </form>

      <RecoveryCodesDialog open={showCodes} onClose={() => setShowCodes(false)} />
    </div>
  );
}

function ActiveView() {
  const disable = useDisableTwoFactor();
  const codes = useTwoFactorRecoveryCodes();
  const regenerate = useRegenerateRecoveryCodes();
  const gate = usePasswordGate();
  const [showCodes, setShowCodes] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);

  return (
    <div className="space-y-3 text-sm">
      <p>You'll be prompted for a 6-digit code on every sign-in.</p>

      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          onClick={() =>
            gate(async () => {
              await codes.refetch();
              setShowCodes(true);
            })
          }
        >
          <KeyRound className="size-4" /> View recovery codes
        </Button>
        <Button
          variant="secondary"
          onClick={() => gate(() => regenerate.mutateAsync())}
          disabled={regenerate.isPending}
        >
          <RefreshCw className="size-4" /> Regenerate codes
        </Button>
        <Button variant="danger" onClick={() => setConfirmDisable(true)}>
          <Trash2 className="size-4" /> Disable two-factor
        </Button>
      </div>

      <Dialog open={showCodes} onClose={() => setShowCodes(false)} title="Recovery codes">
        <p className="text-sm text-[var(--color-foreground-muted)]">
          Store these somewhere safe. Each code can be used once if you lose your authenticator.
        </p>
        <ul className="mt-3 grid grid-cols-2 gap-2 font-mono text-sm">
          {(codes.data ?? []).map((c) => (
            <li key={c} className="rounded bg-[var(--color-surface-muted)] px-2 py-1">
              {c}
            </li>
          ))}
        </ul>
      </Dialog>

      <Dialog
        open={confirmDisable}
        onClose={() => setConfirmDisable(false)}
        title="Disable two-factor?"
      >
        <p className="text-sm text-[var(--color-foreground-muted)]">
          You'll only need your password to sign in. You can re-enable it any time.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setConfirmDisable(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={disable.isPending}
            onClick={() =>
              gate(async () => {
                await disable.mutateAsync();
                setConfirmDisable(false);
              })
            }
          >
            {disable.isPending ? 'Disabling…' : 'Disable'}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function RecoveryCodesDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const codes = useTwoFactorRecoveryCodes();

  useEffect(() => {
    if (open) codes.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} title="Save your recovery codes">
      <p className="text-sm text-[var(--color-foreground-muted)]">
        Two-factor is now active. Store these codes somewhere safe — each one can be used once if
        you lose your authenticator.
      </p>
      <ul className="mt-3 grid grid-cols-2 gap-2 font-mono text-sm">
        {(codes.data ?? []).map((c) => (
          <li key={c} className="rounded bg-[var(--color-surface-muted)] px-2 py-1">
            {c}
          </li>
        ))}
      </ul>
      <div className="mt-4 flex justify-end">
        <Button onClick={onClose}>I've saved them</Button>
      </div>
    </Dialog>
  );
}
